// Modal for sending money to anyone via PayPal Payouts (email-based).
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { sendPaypalPayout } from "@/lib/paypal";
import { parseAmountToCents, formatMoney } from "@/lib/money";
import { toast } from "sonner";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  prefillEmail?: string;
  onSent?: () => void;
};

export const SendPaypalDialog = ({ open, onOpenChange, prefillEmail = "", onSent }: Props) => {
  const [email, setEmail] = useState(prefillEmail);
  const [amount, setAmount] = useState("10.00");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const cents = parseAmountToCents(amount) ?? 0;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || cents <= 0) {
      toast.error("Enter a recipient email and amount");
      return;
    }
    setSubmitting(true);
    try {
      const r = await sendPaypalPayout({
        recipientEmail: email,
        amountCents: cents,
        note,
      });
      toast.success(`PayPal payout queued (${r.status})`);
      onSent?.();
      onOpenChange(false);
      setAmount("10.00");
      setNote("");
    } catch (err) {
      toast.error((err as Error).message || "Payout failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Send via PayPal</DialogTitle>
          <DialogDescription>
            Sends money straight to a PayPal account by email. Recipient gets an email
            from PayPal to claim funds.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="pp-email">Recipient PayPal email</Label>
            <Input
              id="pp-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="friend@example.com"
              className="mt-1.5"
              required
            />
          </div>
          <div>
            <Label htmlFor="pp-amount">Amount (USD)</Label>
            <Input
              id="pp-amount"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="mt-1.5"
            />
          </div>
          <div>
            <Label htmlFor="pp-note">Note (optional)</Label>
            <Input
              id="pp-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={140}
              className="mt-1.5"
            />
          </div>
          <Button type="submit" disabled={submitting} className="w-full" variant="hero">
            {submitting
              ? <Loader2 className="h-5 w-5 animate-spin" />
              : `Send ${formatMoney(cents)} via PayPal`}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
